import { fn, IInfOperation } from '../utils';
import { CompatibilityService } from '../services/compatibilityservice';
import { IPageObj, IPageProperty, IObservableArray, Model, ObservableArray, PagePropertyType, NavGridVisibility, IPageQueryConstraint } from '../nav';
import { NavClientService, INavPageClientInfo, IPageQueryResult, Observable, TNavUrlAccess } from '../navdata';
import { constants as kconstants, ReplaceObservableData, CompileFilters, AsObservableArray } from '../navkendo';
import {
    IFDRW_WF_User, IFDRW_Workflow_Running_Instances, IFDRW_Workflow_User_Instances_List,
    IFDRW_Workflow_Setup_WS, IFDRW_Workflow_Users,
    Pages as rwfpages,
    AccountingMode
} from './rwfpages.generated';
import { IsKameral } from './rwf.functions';
import { RwfBootstrap, IRwfBootstrapParameters } from './rwf.bootsrap';
import { FDRW_Workflow_Running_Instances } from '@common/rwf/rwf.const';
import { filter } from 'rxjs/operators';
import { FwfDataService, IWFDataItem } from './fwfdata.service';

export type InvoiceDataListType = "MyInvoices" | "AllInvoices" | "InvolvedInvoices" | "FWF" | "FWFAll";
export type RwfInvoiceDataItem = (IFDRW_Workflow_User_Instances_List & IFDRW_Workflow_Running_Instances) & Model;

/** Event Triggered when InvoiceData Change took Place  */
export const Event_InvoiceDataRefresh = "InvoiceDataRefresh";

/** Event triggered when 'Apply Filter is called' */
export const Event_ApplyFilter = "applyFilter";

export const Event_InvoiceDataRequested = "$KWF.InvoiceDataRequested";

/** Filter Definition */
export interface IncoreFilter {
    /**  ID of Search Configuration */
    ID: string;

    /** Title of Search Configuration */
    Title: string;

    /** Description for Filter (TODO : use as Hint?) */
    Description?: string;

    /** kendo Filter Definitions */
    Filters: DataSourceFilters<IFDRW_Workflow_Running_Instances>;

    /** Determination Function build from given filters */
    Predicate: (p: any) => boolean;

    /**  Currently Selected Filter */
    Selected?: boolean;

    /** Do not Visualize if none Available */
    HiddenIfNone: boolean;

    CustomData?: boolean;
}

export const ID_ProcessErros = "ProcessErrors";
export const ID_ProcessPending = "Processing";

/** Invoice Data Handling */
export class RwfInvoiceDataService {

    public NavUrl: TNavUrlAccess;
    public readonly FWFData : FwfDataService;
    public readonly NavClient: NavClientService;
    constructor(
        readonly $c: CompatibilityService,
        rwfContext: RwfBootstrap | PromiseLike<IRwfBootstrapParameters>,
    ) {
        this.NavClient = $c.resolve("NavClient");
        this.FWFData = $c.resolve("Rwf")("FwfDataService");
        const rwfBootstrap = fn.isPromise(rwfContext) ? rwfContext : rwfContext.Resolve;
        const resolveBootstrap = rwfBootstrap.then(context => {
            this.NavUrl = context.NavUrlInfo;
            this.Filters = CreateInvoiceFilters(context.RwfSetup);
            this.UserInfo = context.RwfUser || null;
            return context;
        });

        const resolveAdditionalFilters = rwfBootstrap.then(context => {
            const currUserId = context && context.RwfUser.ID;
            if (!currUserId) {
                return;
            }
            const errFilter = (byMe: boolean) => CreateInvoiceFilter({
                id: ID_ProcessErros,
                title: 'Fehler',
                description: (byMe ? 'von mir ' : '') + 'angeforderte, fehlerhafte Weiterleitungen',
                kendoFilter: (<InvoiceFilterItem[]>[
                    { field: 'ProcessRouting', operator: 'eq', value: false },
                    { field: 'RoutingError', operator: 'neq', value: '' },
                    byMe ? { field: "LastChangedBy", value: currUserId } : undefined
                ]).filter(fn.isSet),
                hiddenIfNone: true
            });

            return {
                MyInvoices: [CreateInvoiceFilter({
                    id: "AssignedToMe",
                    title: "Mir zugewiesen",
                    description: "Belege, die mir direkt zugewiesen sind",
                    kendoFilter: [{ field: "CurrentWorkflowUser", operator: "eq", value: currUserId }],
                    hiddenIfNone: true
                }), errFilter(false)],
                InvolvedInvoices: context.RwfSettings.User.IsStandalone && [CreateInvoiceFilter(
                    {
                        id: ID_ProcessPending,
                        title: 'Ausstehende',
                        description: 'von mir angeforderte, noch nicht durchgeführte Weiterleitungen',
                        kendoFilter: [
                            { field: 'ProcessRouting', operator: 'eq', value: true },
                            { field: 'CheckedOutBy', operator: 'eq', value: currUserId },
                        ],
                        hiddenIfNone: true
                    }), errFilter(true)],
                FWFFilters : [CreateInvoiceFilter({
                    id: "AssignedToMe",
                    title: "Mir zugewiesen",
                    description: "Belege, die mir direkt zugewiesen sind",
                    kendoFilter: [{ field: "CurrentWorkflowUser", operator: "eq", value: currUserId }],
                    hiddenIfNone: true
                }), errFilter(false)]
            };
        });

        const rwfBackend = $c.resolve("RwfBackend");

        // We Must Ensure Users are Loaded for Customization of Displayed Info
        const ensureUserInfosLoaded = resolveBootstrap.then(context =>
            rwfBackend.LoadWorkflowUsers(this.NavUrl).then(wfUsers =>
                RwfUsersPerUrlInfo[context.NavUrlInfo.FullUrl] = fn.ToDictionary(wfUsers || [], "ID")
            )).then(fn.noop, fn.noop); // do not propagete user info load error

        // Setup Needed Resolves
        this.Resolve = $c.all([resolveBootstrap, ensureUserInfosLoaded, resolveAdditionalFilters]).then(() => $c.all([
            this.GetPageClient("AllInvoices").then(all =>
                this.AllInvoices = new RwfInvoiceData(this, all, this.Filters, "AllInvoices")),
            this.GetPageClient("MyInvoices").then(my =>
                resolveAdditionalFilters.then(addFilter =>
                    this.MyInvoices = new RwfInvoiceData(this, my, this.Filters.concat(addFilter && addFilter.MyInvoices || []), "MyInvoices"))),
            this.GetPageClient("InvolvedInvoices").then(inv =>
                resolveAdditionalFilters.then(addFilter =>
                    this.InvolvedInvoices = new RwfInvoiceData(this, inv, this.Filters.concat(addFilter && addFilter.InvolvedInvoices || []), "InvolvedInvoices"), fn.noop)),
                    this.FWFData.IsFWFActive && this.GetPageClient("FWF").then(inv =>
                      resolveAdditionalFilters.then(addFilter => this.FWFItems = new RwfInvoiceData(this, inv, addFilter?.FWFFilters || [], "FWF"))),
                    this.FWFData.IsFWFActive && this.GetPageClient("FWFAll").then(inv =>
                      resolveAdditionalFilters.then(addFilter => this.FWFAllItems = new RwfInvoiceData(this, inv, addFilter?.FWFFilters || [], "FWFAll")))
                      ]).then(() => {
            this.$c.Events.next({ Event: Event_InvoiceDataRequested });
            return this;
        }));

        this.ActRefreshInvoiceData = this._actRefreshInvoiceData();
    }

    /** Promise to Force Initialization, other Properties only available after! */
    Resolve: PromiseLike<RwfInvoiceDataService>;

    /** Aktueller Benuter */
    UserInfo?: IFDRW_WF_User;

    /** Meine Belege */
    MyInvoices: RwfInvoiceData;

    /** Rechnungseinganngsbuch */
    AllInvoices: RwfInvoiceData;

    /** Meine durchlaufenen Belege (Compatib. : Not Available on '<18' Nav Versions */
    InvolvedInvoices?: RwfInvoiceData;

    /** 'Meine' Belege des Freigabeworkflows */
    FWFItems?: RwfInvoiceData;

    /** 'Alle' Belege des Freigabeworkflows */
    FWFAllItems? : RwfInvoiceData;

    /** Action To Refresh InvoiceData (MyInvoices, AllInvoices) */
    readonly ActRefreshInvoiceData: IInfOperation & {
        /**  Execute */
        Refresh(): PromiseLike<any>;
        /** Activate "AutoRefresh" Functionality on Edit Change of Other Users:
            - if document not focus : refresh in max 5 sec. cycle, if not has focus: wait for focus and refresh then ....
            - returns function to Deactivate again
        */
        ActivateAutoRefresh(timeout?: number): () => void;
        /** At least one Server Side Notification of edit Changes has been received after last Refresh */
        Pending?: boolean;
    };

    /** Promise Resolved After Current Running Data Request Done (or immediately if none running) */
    public CurrentRefresh(): PromiseLike<any> {
        return this.$c.all(this.currRunning);
    }

    /** Refresh All Invoice Data */
    public RefreshInvoiceData(forceRefresh?: boolean) {
        if (forceRefresh) {
            delete this.currRunning;
        }
        return this.$c.all([this.AllInvoices, this.MyInvoices, this.InvolvedInvoices, this.FWFItems, this.FWFAllItems].filter(fn.isSet).map(d => d.GetData(forceRefresh)));
    }

    public GetPageClient(listType: InvoiceDataListType) {
        const NavClient = this.NavClient;
        return NavClient.GetPageClientInfo(FDRW_Workflow_Running_Instances, this).then(client => client,
            err => {
                // Falback if 'new', common page is not available
                var pageName = listType == "AllInvoices" ? rwfpages.FDRW_Workflow_Invoice_Ledger.PageName :
                    (listType == "MyInvoices" ? rwfpages.FDRW_Workflow_User_Instances_List.PageName : "");

                if (!pageName)
                    throw err;

                return NavClient.GetPageClientInfo(pageName, this);
            });
    }

    public ReloadInvoiceData(listType: InvoiceDataListType): PromiseLike<IPageQueryResult<IFDRW_Workflow_Running_Instances>> {
        const NavClient = this.NavClient;
        return this.GetPageClient(listType).then(p => {
            var isCommon = p.PageName == FDRW_Workflow_Running_Instances;
            var storeName = isCommon ? "Common" : p.PageName;
            this.currRunning = this.currRunning || {};
            var curr: PromiseLike<IPageQueryResult<IPageObj>> = this.currRunning[storeName] || (this.currRunning[storeName] =
                NavClient.ReadMultiple({ PageName: p.PageName, Constraints: [], PageCount: 0 }, this));
            return curr.then(c => {
                let data = c.Data as IFDRW_Workflow_Running_Instances[];
                let filterProp: keyof IFDRW_Workflow_Running_Instances | "" = listType == "AllInvoices" ?
                    "InvoiceLegder" : (listType == "InvolvedInvoices" ? "Involved" : (listType == "MyInvoices" ? "MyDocument" :
                      (listType == "FWF" ? "WorkflowType" : (listType == "FWFAll" ? "WorkflowType" : ""))));

                if (isCommon) {
                    data = data.filter(item => item[filterProp]);
                    if (listType == "AllInvoices" || listType == "MyInvoices") {
                        //  not FWF  items here
                        data = data.filter(item => item.AccountingMode != AccountingMode.FWF);
                    } else if(listType == "FWF") {
                        // TODO: refactor 'filterProp' --> FWF: additional filter MyDocument
                        data = data.filter(item => item.MyDocument)
                    } else if(listType == "FWFAll") {
                        // exclude "Involved only items"
                        data = data.filter(item => item.InvoiceLegder || item.MyDocument);

                    }
                } else {
                    data.forEach(item => item[filterProp] = true);
                }
                return {
                    Bookmark: c.Bookmark,
                    Data: data,
                    Client: <any>c.Client
                };
            });
        });
    }
    private currRunning: { [page: string]: PromiseLike<IPageQueryResult<IPageObj>> };

    /** the possible Filter Definitions on Invoice Data (fix at the moment, ....) */
    private Filters?: IncoreFilter[];

    FieldMap = {
        CurrStep: "CurrentWorkflowStep_Name",
        DueDate: "Due_Date",
        Created: "Created",
        Amount: "Amount",
        Accounting: "Accounting"
    };

    /** Called after data got fetched from server */
    PrepareInvoiceData(data: any[] | IObservableArray<any>) {
        const Filters = this.Filters;
        if (Filters && Filters.length > 0) {
            data.forEach((item: InvoiceDataItem) => {
                var filterinfo = item._filter || (item._filter = {});
                Filters.forEach(filter => {
                    filterinfo[filter.ID] = filter.Predicate(item);
                });
            });
        }

        this.FWFData.NormalizeInvoicedata(data)
    }

    /** Resolve User Name by UserId (KWF3 Scenrio Only!, Fallback : userId) */
    public static ResolveUser(userId: string, navUrl: string) {
        if (!userId)
            return userId;

        var map = RwfUsersPerUrlInfo[navUrl],
            mapUser = map && map[userId];
        return (mapUser && mapUser.UserName) || userId;
    }

    /** All NAV users (technical user mapped to friendly user name) as array */
    public static GetUsers(navUrl: string): IFDRW_Workflow_Users[] {
        var perUrlInfo = RwfUsersPerUrlInfo[navUrl];
        return perUrlInfo && fn.DictToArray(perUrlInfo);
    }

    private _actRefreshInvoiceData() {
        const $c = this.$c;
        const me = this;
        let lastActivate = fn.noop;

        const actRefresh = {
            Enabled: true,
            Execute: () => refreshInvoiceData(),
            Refresh: () => refreshInvoiceData(),

            ActivateAutoRefresh: (timeout?: number) => {
                lastActivate();
                if (!(timeout > 0))
                    timeout = 5000;
                return lastActivate = $c.resolve("Rwf")("RwfApplication").RegisterEditChange(fn.throttle(fn.proxy(OnEditChange, null, false), timeout));
            },
            Pending: <boolean>undefined,
        };

        //MSQQQ Still necessary?
        // Caution: "GetInvoiceData" should not be called initially: (KWF2 scenario wOut userDesk!), only Allow Action after first Request!
        // so defer Enabling User Refresh Invoices Request after first imp Refresh is done
        var subscription = $c.Events.pipe(filter(e => e && e.Event === Event_InvoiceDataRequested)).subscribe((s) => {
            subscription.unsubscribe();

            $c.finally(this.CurrentRefresh(),
                () => actRefresh.Enabled = true);
        });

        return actRefresh;

        /** Refresh Invoice Data Action */
        function refreshInvoiceData(this: void) {
            if (!actRefresh.Enabled)
                return $c.when<any>(0);

            actRefresh.Enabled = false;
            return $c.finally(me.RefreshInvoiceData(true), () => actRefresh.Enabled = true);
        };

        function setWaitForActivate(this: void, activate: boolean) {
            if (activate != !!actRefresh.Pending) {
                actRefresh.Pending = activate;
                $("body").off(".navWaitForActivate");

                if (actRefresh.Pending) {
                    $("body").on("focus.navWaitForActivate activate.navWaitForActivate keypress.navWaitForActivate mousemove.navWaitForActivate", () => {
                        setWaitForActivate(false);
                        OnEditChange(true);
                    });
                }
            }
        }
        function OnEditChange(this: void, forceImmediate?: boolean) {
            if (!actRefresh.Enabled)
                return;

            if (forceImmediate || window.document.hasFocus()) {
                actRefresh.Execute();
                return;
            }

            setWaitForActivate(true);
        }
    }
}
/** Invoice Data Item Data Filter State */
export interface InvoiceDataItem {
    _filter?: { [filterId: string]: boolean };
    FWFData?: IWFDataItem [] & { __Handled?: boolean };
}

/** Nav Based Invoice Data */
export class RwfInvoiceData extends Observable {
    private getData: PromiseLike<IObservableArray<RwfInvoiceDataItem>>;

    private loadingData: PromiseLike<IObservableArray<RwfInvoiceDataItem>>;

    constructor(public InvoiceData: RwfInvoiceDataService, public readonly Client: INavPageClientInfo, public readonly Filters: IncoreFilter[],
        public readonly ListType: InvoiceDataListType) {
        super();

        this.Fields = this.Client.MetaData.Properties;
        if (this.ListType == "InvolvedInvoices") {
            // Assumption ....!!!!
            this.Fields = this.Fields.filter(field => !field.IsCustomExclude);
        };

        if (ListType == "FWF" || ListType == "FWFAll") {
            var extFilters = Filters.slice();
            var TileFilters = this["TileFilters"] = extFilters.slice();
            let refreshFWFGroups = () => {
                var groupFilters: IncoreFilter[] = [];
                var filterGroups = fn.sort(fn.DictToArray(fn.GroupBy(this.Items, "WorkflowType")).filter(item => item[0].WorkflowType), item => item.length);
                var currInfos = filterGroups.map(g => ({ Code : g[0].WorkflowType, Items: g }));
                currInfos.forEach((currInfo, idx) => {
                    const items = currInfo.Items;
                    const code = currInfo.Code;
                    var description = items[0].WorkflowTypeDescription || code;
                    var curr: IncoreFilter = fn.FindFirst(Filters, "ID", code);
                    if (!curr) {
                        curr = CreateInvoiceFilter({
                            id: code,
                            title: description,
                            description: description,
                            kendoFilter: [{ field: "WorkflowType", value: code }]
                        });
                        curr.CustomData = true;
                        Filters.splice(0, 0, curr);
                    }
                    groupFilters.splice(0, 0, curr);
                    this.FilterCount[curr.ID] = items.length;
                });

                // refresh others / orphaneds
                Filters.forEach(filter => {
                    if (!fn.FindFirst(currInfos, "Code", filter.ID )) {
                        this.FilterCount[filter.ID] = isNaN(this.TotalCount) ? NaN : this.Items.filter(filter.Predicate).length;
                    }
                });

                var newTileFilters = extFilters.filter(item => item.HiddenIfNone !== true || this.FilterCount[item.ID] > 0);
                const MyInvoices = this.InvoiceData.MyInvoices;
                var myInvFCount = MyInvoices.Filters.filter(item => item.HiddenIfNone ? MyInvoices.FilterCount[item.ID] > 0 : true).length;
                newTileFilters.splice(0, 0, ...groupFilters.slice(0, Math.max(4, myInvFCount) - newTileFilters.length));
                TileFilters.splice(0, 0, ...newTileFilters);

            };
            this.bind(Event_InvoiceDataRefresh, refreshFWFGroups);
        } else {
            Filters.forEach(filter => {
                this.FilterCount[filter.ID] = NaN;
                const fields = this.Fields;
                var filterArray = filter.Filters;
                var arr = Array.isArray(filterArray) ? (filterArray as kendo.data.DataSourceFilterItem[]): [];
                var invalidFilters = arr.map((f : kendo.data.DataSourceFilterItem) => fields.filter(field => field.Name == f.field).length).filter(item => item == 0);
                // Only Calculate if all Filters seem valid ...
                if (invalidFilters?.length == 0) {
                    let refreshFilters = () => (this.TotalCount >= 0) && (this.FilterCount[filter.ID] = this.Items.filter(item => filter.Predicate(item)).length);
                    this.bind(Event_InvoiceDataRefresh, refreshFilters);
                    refreshFilters();
                }
            });
        }
    }

    public ApplyFilter(item: IncoreFilter | string) {
        var filters = this.Filters;
        if (typeof item === "string") {
            item = fn.FindFirst(filters, "ID", item);
        }
        this.SelectedFilter = item;
        filters.forEach(f => f.Selected = (item == f));
        this.trigger(Event_ApplyFilter, { Filter: item });
    }

    SelectedFilter?: IncoreFilter;

    public readonly Items: IObservableArray<RwfInvoiceDataItem> = AsObservableArray([]);

    public GeItem(item: string | number | IFDRW_Workflow_User_Instances_List | IFDRW_Workflow_Running_Instances): RwfInvoiceDataItem {
        const Items = this.Items;
        if (typeof (item) === "string") {
            if (item) {
                let no: number;
                let result = Items.find(check => check.ID == item);
                if (!result && (no = parseInt(item)) > 0 && !isNaN(no)) {
                    result = this.GeItem(no);
                }
                return result;
            }
        } else if (typeof (item) == "number") {
            let no = item;
            return Items.find(item => item.EntryNo === no);
        } else if (item) {
            return this.GeItem(item.ID);
        }
    }

    // Is dataItem editable by current user (and present in myInvoice)?
    public IsItemEditable(dataItem: IFDRW_Workflow_User_Instances_List & IFDRW_Workflow_Running_Instances, checkIsInMyInvoice = false, forceIsInMyInvoice = false) {
        if (!dataItem)
            return false;

        // Checked out by other user?
        if (dataItem.CheckedOutBy && this.InvoiceData.UserInfo && this.InvoiceData.UserInfo.ID != dataItem.CheckedOutBy)
            return false;

        if(dataItem.WorkflowType && dataItem.FWFData?.length> 0) {
            const check = dataItem.MyDocument;
            if(typeof(check) === "boolean" && !check) {
              return false;
            }
        }
        // Assigned to other user?
        //if (dataItem.CurrentWorkflowUser && this.InvoiceData.MyUserInfo.ID != dataItem.CurrentWorkflowUser)
        //    return false;


        if (!forceIsInMyInvoice && checkIsInMyInvoice) {
            var id = dataItem.ID;
            var myInvoiceContainsID = this.InvoiceData.MyInvoices.Items.filter(item => item && item["ID"] == id).length > 0;
            return myInvoiceContainsID;
        }

        return true;
    };

    /** Get Data Items */
    GetData(forceRefresh?: boolean) {
        if ((forceRefresh == true && !this.loadingData) || !this.getData) {
            var items = this.Items;
            this.getData = this.loadingData = this.InvoiceData.ReloadInvoiceData(this.ListType).then(d => {
                var typedValidatedData = d.Data.map(item => <Model & IFDRW_Workflow_Running_Instances>new d.Client.MetaData.Model(item));
                var keyFunc = (item) => d.Client.MetaData.GetClientKey(item, false);
                // Compare items by LastChanged/CheckedOutBy/WFStep is way faster then Default (compare by 'JSON.stringify') --> Relevant for large Sets
                var compareFunc = (a: IFDRW_Workflow_Running_Instances, b: IFDRW_Workflow_Running_Instances) =>
                    // Key ändert sich nicht falls sich nur berechnte Felder ändern!
                    (a.Key == b.Key) && (a.LastChanged && a.LastChanged.getTime()) == (b.LastChanged && b.LastChanged.getTime())
                    && (a.CheckedOutBy == b.CheckedOutBy) && (a.CurrentWorkflowStep == b.CurrentWorkflowStep);
                var hasChanges = ReplaceObservableData(items, <any>typedValidatedData, keyFunc, compareFunc);
                if (hasChanges || isNaN(this.TotalCount)) {
                    this.TotalCount = items.length;
                    this.trigger(Event_InvoiceDataRefresh);
                }
                return items;
            });

            const resetLoading = () => {
                if (this.getData == this.loadingData) {
                    this.loadingData = null;
                }
            };
            this.getData.then(resetLoading, resetLoading);
        }
        return this.getData;
    }

    public readonly Fields: IPageProperty[];

    /** Current FilterCount per Filter Definition state */
    FilterCount: { [filterId: string]: number } = {};

    /** Current Total ItemCount */
    TotalCount: number = NaN;
}

/** Create Filter Item */
function CreateInvoiceFilter(config: { id: string; title: string; description?: string; kendoFilter: DataSourceFilter<IFDRW_Workflow_Running_Instances>; hiddenIfNone?: true }): IncoreFilter {
    var result = <IncoreFilter>{
        ID: config.id,
        Title: config.title,
        Description: config.description,
        Count: Number.NaN,
        Filters: config.kendoFilter,
        Predicate: CompileFilters(config.kendoFilter),
        HiddenIfNone: !!config.hiddenIfNone
    };

    return result;
}
/**  Create Default Invoice Filters */
function CreateInvoiceFilters(wfSetup: IFDRW_Workflow_Setup_WS) {
    var filters = new Array<IncoreFilter>();
    const isKameral = IsKameral(wfSetup);
    const DocumentType = "DocumentType";
    let DocTypeReservation = isKameral ? "Mittelreservierung" : "Einkaufsbestellung",
        ReservationTitle = isKameral ? "Aufträge" : "Bestellungen",
        reservationFilter = {
          logic : "or",
          filters : [
            <InvoiceFilterItem>{ field: DocumentType, operator: "eq", value: DocTypeReservation },
            <InvoiceFilterItem>{ field: DocumentType, operator: "eq", value: "Einkaufsbestellung (Änderung)" }
          ]
        },
      noReservationFilter = [
          <InvoiceFilterItem>{ field: "DocumentType", operator: "startswith", value: "" },
          <InvoiceFilterItem>{ field: DocumentType, operator: "ne", value: DocTypeReservation },
          <InvoiceFilterItem>{ field: DocumentType, operator: "ne", value: "Einkaufsbestellung (Änderung)" }, // doppisch
            //{ field: "DocumentType", operator: "ne", value: "Einkaufsbestellung (Änderung)" }, // kameral TODO
        ];

    // Set the Fix Filters at the Moment

    filters.push(CreateInvoiceFilter({
        id: "CriticalAmount",
        title: "Große Rechnungen",
        description: "Rechnungen ab " + kendo.format("{0:c0}", wfSetup.CriticalAmount),
        kendoFilter: fn.FlattenArray([[
            { field: "Amount", operator: "gte", value: wfSetup.CriticalAmount }],
            noReservationFilter])

    }));

    var dueDate = new Date(); dueDate.setDate(dueDate.getDate() + wfSetup.CriticalDue);
    filters.push(CreateInvoiceFilter({
        id: "CriticalDue",
        title: "Fäll. Rechnungen bis " + kendo.format(kconstants.KendoShortDateFormat, dueDate),
        description: "Fällige Rechnungen (ab " + wfSetup.CriticalDue + " Tage vor Frist)",
        kendoFilter: fn.FlattenArray([
            [
                { field: "Due_Date", operator: "neq", value: null },
                { field: "Due_Date", operator: "lte", value: dueDate }
            ],
            noReservationFilter
        ])
    }));

    var longdate = fn.GetDateWithoutTime(new Date()); longdate.setDate(longdate.getDate() - wfSetup.CriticalDuration + 1);
    filters.push(CreateInvoiceFilter({
        id: "CriticalDuration",
        title: "Langlaufende Rechnungen",
        description: "Rechnungen, die sich schon " + wfSetup.CriticalDuration + " Tage und länger im Workflow befinden",
        kendoFilter: fn.FlattenArray([
            [
                { field: "Created", operator: "lte", value: longdate }
            ],
            noReservationFilter
        ])
    }));

    if (wfSetup.IsBWFActive) {
        filters.push(CreateInvoiceFilter({
            id: "Reservation",
            title: ReservationTitle,
            description: ReservationTitle,
            kendoFilter: reservationFilter
        }));
    }

    return filters;
}

interface DataSourceFilter<T> extends kendo.data.DataSourceFilter {

}

interface DataSourceFilterItem<T> extends kendo.data.DataSourceFilter {
  operator?: string|Function;
  field?: keyof T & string;
  value?: any;
}

interface DataSourceFilters<T> extends kendo.data. DataSourceFilter {
  logic?: string;
  filters?: DataSourceFilter<T>[];
}
type InvoiceFilterItem = DataSourceFilterItem<IFDRW_Workflow_Running_Instances>;

/** Static stored RWF User Information per Nav 'FullUrl'  */
const RwfUsersPerUrlInfo: Record<string, Record<string, IFDRW_Workflow_Users>> = {};
